import { useCallback, useState } from "react";
import type React from "react";
import type { MutableRefObject } from "react";
import {
  useGenerateVariantB,
  useApplyBrandSignal,
  useApplyKeyword,
  useApplyFormatTreatment,
  useApplyScorecardGap,
  useGenerateRationale,
  useParseFieldZones,
} from "@/resources/ab-test/ab-test.api";
import { toast } from "@/hooks/use-toast";
import { ApiError } from "@/services/api.service";
import type { ContentType, GenerateRationaleResponse, KeywordAdjustment, UnfulfilledLever } from "@/types/ab-tests";
import type { CosmoQuestion } from "@/types/scorecard";
import { COSMO_CATEGORY_LABELS } from "@/types/scorecard";
import type { StructureKey } from "@/components/ab-tests/StructurePanel";
import type { ScorecardGapSnapshot, KeywordPrefill } from "@/components/ab-tests/create-test-modal.types";
import type { SeoStrategyRecommendation } from "@/types/seo-strategy";
import { getStatusConfig } from "@/components/spi/SPIScorecard/constants";
import { isKeywordMetricPending, KEYWORD_PENDING_LABEL } from "@/utils/keywordPendingDisplay";

/** AUT-430 (WEB-010): the Failure paths table's `{message}` -- the ApiError's own body message,
 * never its generic `"{status} {statusText}"`, with `AI gateway error.` as the fallback both when
 * the error isn't an ApiError (network failure) and when its body carries no message. */
function extractApplyErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const data = err.data as { message?: string } | undefined;
    if (data && typeof data.message === "string" && data.message) return data.message;
  }
  return "AI gateway error.";
}

/** AUT-464: a bank metric is `-1` while it is still being calculated (`isKeywordMetricPending`) --
 * a sentinel, never a value to print or to hand the AI as one. */
type KeywordMetric = { kind: "value"; value: number } | { kind: "pending" } | null;

function readKeywordMetric(value: number | null | undefined): KeywordMetric {
  if (value == null) return null;
  if (isKeywordMetricPending(value)) return { kind: "pending" };
  return { kind: "value", value };
}

function fieldLabel(field: string): string {
  return field === "title" ? "Title" : `Bullet ${field.split("-")[1]}`;
}

/** AUT-463: whitespace-insensitive, so a model that only re-spaces a field has not rewritten it. */
function normaliseFieldText(text: string | undefined): string {
  return (text ?? "").replace(/\s+/g, " ").trim();
}

interface UseVariantBActionsParams {
  parsedProductId: string;
  selectedBrandId: number | null | undefined;
  selectedFields: ContentType[];
  hypothesis: string;
  targetPhrase: string;
  structureTreatments: Partial<Record<StructureKey, string | null>>;
  variantATitle: string;
  /** AUT-463: Variant A's bullets -- the base the apply prompts rewrite, so the base a field is
   * compared against to decide whether it was actually rewritten. */
  variantABullets: Record<string, string>;
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  appliedScorecardGaps: string[];
  lastSource: string | null;
  lastSourceDetail: string;
  pushHistory: () => void;
  setIsGenerating: (v: boolean) => void;
  setLastSource: (v: string) => void;
  setLastSourceDetail: (v: string) => void;
  /** AUT-430 (WEB-010): FR-10's hypothesis and FR-14's -- the two applies that replace it. */
  setHypothesis: (v: string) => void;
  /** AUT-430 (WEB-010): FR-10 -- a successful keyword apply sets the test's target keyword. */
  setTargetPhrase: (v: string) => void;
  /** AUT-430 (WEB-010): FR-15 -- only a successful `Use this strategy` writes this. */
  setStrategyLinkId: (v: number | null | undefined) => void;
  /** AUT-430 (WEB-003/WEB-010): the Variant B header's AI status alert. `null` shows nothing once
   * `isGenerating` is false; `"applying"` is transient (invisible again once `isGenerating` goes
   * false unless a success moves it to `"applied"`); only a successful strategy apply does that. */
  setAiStatus: (v: { state: "applying" | "applied"; text: string } | null) => void;
  setAppliedScorecardGaps: React.Dispatch<React.SetStateAction<string[]>>;
  setGapSnapshots: React.Dispatch<React.SetStateAction<Record<string, ScorecardGapSnapshot>>>;
  setVariantBTitle: (v: string) => void;
  setVariantBBullets: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  setVariantBFieldSources: (fn: (prev: Record<string, "ai" | "ai-edited" | "manual">) => Record<string, "ai" | "ai-edited" | "manual">) => void;
  setParsedZones: (fn: (prev: Record<string, import("@/types/ab-tests").ParsedField | null>) => Record<string, import("@/types/ab-tests").ParsedField | null>) => void;
  setParsingFields: (fn: (prev: Set<string>) => Set<string>) => void;
  setIsGeneratingRationale: (v: boolean) => void;
  setRationaleData: (data: GenerateRationaleResponse | null) => void;
  setRationaleOpen: (v: boolean) => void;
  /**
   * AUT-430 (WEB-009): the counter useCreateTestLogic's automatic-hypothesis effect races
   * against. Only the two applies that write the hypothesis (keyword, strategy -- WEB-010) may
   * increment it, through `incrementHypothesisSeq` below; nothing else writes it.
   */
  applyHypothesisSeqRef: MutableRefObject<number>;
}

export function useVariantBActions({
  parsedProductId,
  selectedBrandId,
  selectedFields,
  hypothesis,
  targetPhrase,
  structureTreatments,
  variantATitle,
  variantABullets,
  variantBTitle,
  variantBBullets,
  appliedScorecardGaps,
  lastSource,
  lastSourceDetail,
  pushHistory,
  setIsGenerating,
  setLastSource,
  setLastSourceDetail,
  setHypothesis,
  setTargetPhrase,
  setStrategyLinkId,
  setAiStatus,
  setAppliedScorecardGaps,
  setGapSnapshots,
  setVariantBTitle,
  setVariantBBullets,
  setVariantBFieldSources,
  setParsedZones,
  setParsingFields,
  setIsGeneratingRationale,
  setRationaleData,
  setRationaleOpen,
  applyHypothesisSeqRef,
}: UseVariantBActionsParams) {
  const generateVariantBMutation = useGenerateVariantB();
  const applyBrandSignalMutation = useApplyBrandSignal();
  const applyKeywordMutation = useApplyKeyword();
  const applyFormatTreatmentMutation = useApplyFormatTreatment();
  const applyScorecardGapMutation = useApplyScorecardGap();
  const generateRationaleMutation = useGenerateRationale();
  const parseFieldZonesMutation = useParseFieldZones();
  const [keywordAdjustment, setKeywordAdjustment] = useState<KeywordAdjustment | null>(null);
  const [unfulfilledLevers, setUnfulfilledLevers] = useState<UnfulfilledLever[] | null>(null);

  const getNumericProductId = () => {
    const id = Number(parsedProductId);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const reparseVariantBZones = useCallback((fields: Record<string, string>) => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId) return;
    for (const [fieldKey, fieldText] of Object.entries(fields)) {
      if (!fieldText) continue;
      setParsingFields(prev => new Set(prev).add(fieldKey));
      const fieldLabel = fieldKey === "title" ? "Title" : `Bullet ${fieldKey.replace("bullet-", "")}`;
      parseFieldZonesMutation.mutateAsync({ productId: numericId, brandId: selectedBrandId, fieldLabel, fieldText })
        .then((res) => { setParsedZones(prev => ({ ...prev, [fieldKey]: { source: fieldText, zones: res.zones } })); })
        .catch(() => {}).finally(() => {
          setParsingFields(prev => { const next = new Set(prev); next.delete(fieldKey); return next; });
        });
    }
  }, [parsedProductId, selectedBrandId]);

  const applyGeneratedFields = useCallback((fields: Record<string, string>) => {
    if (!fields) return;
    const appliedKeys: string[] = [];
    if (selectedFields.includes("title") && fields.title) {
      setVariantBTitle(fields.title);
      appliedKeys.push("title");
    }
    for (const f of selectedFields) {
      if (f.startsWith("bullet-") && fields[f]) {
        appliedKeys.push(f);
      }
    }
    if (appliedKeys.some(k => k.startsWith("bullet-"))) {
      setVariantBBullets(prev => {
        const next = { ...prev };
        for (const f of selectedFields) {
          if (f.startsWith("bullet-") && fields[f]) next[f] = fields[f];
        }
        return next;
      });
    }
    setVariantBFieldSources(prev => {
      const next = { ...prev };
      for (const f of appliedKeys) next[f] = "ai";
      return next;
    });
    // AUT-430 (WEB-009): Variant B's zones are no longer re-parsed on an apply -- reparseVariantBZones
    // stays defined (and returned) below for Variant A's parse; this callsite is the one FR the
    // task removes.
  }, [selectedFields, setVariantBTitle, setVariantBBullets, setVariantBFieldSources]);

  // AUT-463: the apply prompts rewrite the live listing, so a field counts as rewritten only when
  // it differs from Variant A -- never merely because the AI returned it.
  const rewrittenFieldLabels = (fields: Record<string, string>): string[] =>
    selectedFields
      .filter(f => fields[f])
      .filter(f => normaliseFieldText(fields[f]) !== normaliseFieldText(f === "title" ? variantATitle : variantABullets[f]))
      .map(fieldLabel);

  // AUT-430 (WEB-009): the only writer of applyHypothesisSeqRef. WEB-010 calls this from the two
  // applies that replace the hypothesis (keyword, strategy) so useCreateTestLogic's automatic
  // effect discards a run its own apply superseded.
  const incrementHypothesisSeq = useCallback(() => {
    applyHypothesisSeqRef.current += 1;
  }, [applyHypothesisSeqRef]);

  const triggerRationale = useCallback((variantBContent: string) => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId) return;
    setIsGeneratingRationale(true);
    void generateRationaleMutation.mutateAsync({
      productId: numericId, brandId: selectedBrandId,
      hypothesis: hypothesis || undefined,
      leversApplied: undefined,
      variantAContent: variantATitle, variantBContent,
    }).then((res) => { setRationaleData(res); setRationaleOpen(true); })
      .catch(() => {}).finally(() => { setIsGeneratingRationale(false); });
  }, [parsedProductId, selectedBrandId, hypothesis, variantATitle]);

  const handleGenerateMagic = async () => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId || selectedFields.length === 0) return;
    pushHistory(); setIsGenerating(true); setKeywordAdjustment(null); setUnfulfilledLevers(null);
    if (lastSource !== "scorecard") {
      setLastSource("ai-magic");
      setLastSourceDetail("AI-generated best recommendation");
    }
    try {
      const levers = [
        targetPhrase ? `Target keyword: "${targetPhrase}"` : null,
        ...Object.entries(structureTreatments).filter(([, v]) => v !== null).map(([k, v]) => `Format: ${k} = ${v}`),
      ].filter(Boolean).join("\n");
      const result = await generateVariantBMutation.mutateAsync({
        productId: numericId, brandId: selectedBrandId, selectedFields,
        levers: levers || "(no specific levers — generate best recommendation)",
        hypothesis: hypothesis.trim() || "(none provided — infer the goal from the levers)",
        targetKeyword: targetPhrase || undefined,
      });
      applyGeneratedFields(result.fields);
      setKeywordAdjustment(result.keyword_adjustment ?? null);
      setUnfulfilledLevers(result.unfulfilled_levers ?? null);
      toast({ title: "Content generated", description: "Review AI suggestions." });
      triggerRationale(result.fields.title || "");
    } catch {
      toast({ title: "Generation failed", description: "Could not generate content.", variant: "destructive" });
    } finally { setIsGenerating(false); }
  };

  const handleApplySignal = async (signalStatement: string) => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId || selectedFields.length === 0) return;
    pushHistory(); setIsGenerating(true); setLastSource("brand-signal"); setLastSourceDetail(signalStatement); setKeywordAdjustment(null); setUnfulfilledLevers(null);
    try {
      const result = await applyBrandSignalMutation.mutateAsync({
        productId: numericId, brandId: selectedBrandId, selectedFields,
        signalContext: `- Statement: ${signalStatement}`,
      });
      applyGeneratedFields(result.fields);
      const appliedLabels = selectedFields.filter(f => result.fields[f]).map(f => f === "title" ? "Title" : `Bullet ${f.split("-")[1]}`);
      const chosenKw = result.chosenKeyword?.trim() || null;
      const rationaleText = result.rationale?.trim() || `Signal AI applied "${signalStatement}" to ${appliedLabels.join(", ")}${chosenKw ? `, weaving in the phrase "${chosenKw}"` : ""}.`;
      setRationaleData({
        summary: rationaleText,
        signals: [`Brand signal: ${signalStatement}`, ...(chosenKw ? [`Keyword applied: "${chosenKw}"${result.keywordReason ? ` — ${result.keywordReason}` : ""}`] : []), `Fields rewritten: ${appliedLabels.join(", ")}`, ...(result.preserved?.length ? [`Preserved verbatim: ${result.preserved.join("; ")}`] : [])],
        phraseChanges: [], preserved: [], dropped: [],
      });
      setIsGeneratingRationale(false); setRationaleOpen(true);
      toast({ title: "Signal applied", description: "Variant B updated with brand signal strategy." });
    } catch {
      toast({ title: "Failed", description: "Could not apply signal.", variant: "destructive" });
    } finally { setIsGenerating(false); }
  };

  // AUT-430 (WEB-010), FR-10: reworked. `lastSource` / `lastSourceDetail`, the target phrase and the
  // hypothesis move from "before the call" to "on success only", so a failed or empty apply leaves
  // the draft exactly as it was (Failure paths table). `source` defaults to the row-click case;
  // a launch (WEB-012) passes its own `KeywordPrefill["source"]` so a `landscape` pick is not
  // recorded as `keyword`.
  const handleApplyKeywordSource = async (
    kw: { search: string; rank?: number | null; hero_rank?: number | null; search_volume?: number | null; kps?: number | null },
    source: KeywordPrefill["source"] = "keyword",
  ) => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId) return;
    // Belongs to the rationale panel, not the draft -- cleared unconditionally, even when the
    // field guard below is about to return with no request.
    setKeywordAdjustment(null); setUnfulfilledLevers(null);
    if (selectedFields.length === 0) {
      toast({ title: "Select fields first", description: "Choose which fields to weave this keyword into.", variant: "destructive" });
      return;
    }
    // AUT-464: a pending metric reaches neither the AI nor the rationale as `-1`. The AI gets
    // only real values; the rationale says `Pending`, so a keyword with no data yet is not
    // presented as if it had some.
    const rank = readKeywordMetric(kw.hero_rank ?? kw.rank);
    const volume = readKeywordMetric(kw.search_volume);
    const kps = readKeywordMetric(kw.kps);
    const currentRank = rank?.kind === "value" ? rank.value : null;
    const searchVolume = volume?.kind === "value" ? volume.value : null;
    pushHistory(); setIsGenerating(true);
    setAiStatus({ state: "applying", text: "Signal AI is rewriting Variant B…" });
    try {
      const result = await applyKeywordMutation.mutateAsync({
        productId: numericId, brandId: selectedBrandId, selectedFields,
        keywordContext: [`- Keyword: "${kw.search}"`, currentRank != null ? `- Current rank: #${currentRank}` : null, searchVolume != null ? `- Search volume: ${searchVolume.toLocaleString()}/mo` : null, kps?.kind === "value" ? `- KPS: ${Math.round(kps.value)}` : null].filter(Boolean).join("\n"),
      });
      const returnedFields = selectedFields.filter(f => result.fields[f]);
      if (returnedFields.length === 0) {
        toast({ title: "Nothing generated", description: "The AI returned no usable content. Try a different keyword.", variant: "destructive" });
        return;
      }
      // AUT-463: when no field differs from Variant A the AI declined the keyword: the draft,
      // target keyword and hypothesis stay as they were, and the rationale on screen keeps
      // describing the draft that is still there.
      const rewrittenLabels = rewrittenFieldLabels(result.fields);
      if (rewrittenLabels.length === 0) {
        toast({ title: "Keyword not applied", description: `Signal AI couldn't fit "${kw.search}" into the selected fields, so Variant B was left unchanged.`, variant: "destructive" });
        return;
      }
      applyGeneratedFields(result.fields);
      const rationaleText = result.rationale || `Signal AI wove "${kw.search}" into ${rewrittenLabels.join(", ")}${currentRank != null ? ` — ranking #${currentRank}` : ""}${searchVolume != null ? ` with ${searchVolume.toLocaleString()} monthly searches` : ""}.`;
      const signalText = (label: string, metric: KeywordMetric, format: (v: number) => string) =>
        metric == null ? [] : [`${label}: ${metric.kind === "pending" ? KEYWORD_PENDING_LABEL : format(metric.value)}`];
      setRationaleData({
        summary: rationaleText,
        signals: [
          `Keyword: "${kw.search}"`,
          ...signalText("Current rank", rank, v => `#${v}`),
          ...signalText("Search volume", volume, v => `${v.toLocaleString()}/mo`),
          ...signalText("KPS", kps, v => String(Math.round(v))),
          `Fields rewritten: ${rewrittenLabels.join(", ")}`,
          ...(result.preserved?.length ? [`Preserved verbatim: ${result.preserved.join("; ")}`] : []),
        ],
        phraseChanges: [], preserved: [], dropped: [],
      });
      setIsGeneratingRationale(false); setRationaleOpen(true);
      setLastSource(source);
      setLastSourceDetail(kw.search);
      setTargetPhrase(kw.search);
      // AUT-464: with no search volume (none, or still pending) the volume clause is dropped rather
      // than written as "0 monthly searches" -- our wording; FR-10's template always has a volume.
      const volumeText = searchVolume?.toLocaleString("en-US");
      setHypothesis(
        volumeText == null
          ? currentRank != null
            ? `Targeting "${kw.search}" — currently ranking #${currentRank} — will improve discoverability, rank, and qualified traffic.`
            : `Targeting "${kw.search}" — currently unranked — will improve discoverability, rank, and qualified traffic.`
          : currentRank != null
            ? `Targeting "${kw.search}" — a term with ${volumeText} monthly searches currently ranking #${currentRank} — will improve discoverability, rank, and qualified traffic.`
            : `Targeting "${kw.search}" — a term with ${volumeText} monthly searches, currently unranked, — will improve discoverability, rank, and qualified traffic.`,
      );
      incrementHypothesisSeq();
      toast({ title: "Keyword applied", description: "Variant B has been updated." });
    } catch (err) {
      toast({ title: "Generation failed", description: extractApplyErrorMessage(err), variant: "destructive" });
    } finally { setIsGenerating(false); }
  };

  // AUT-430 (WEB-010), FR-14: new. Calls the existing `ai/apply-scorecard-gap` -- no endpoint,
  // prompt or throttle change -- with a gap_context built from the strategy's own saved values
  // rather than a COSMO question's.
  const handleApplyStrategy = async (recommendation: SeoStrategyRecommendation) => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId) return;
    setKeywordAdjustment(null); setUnfulfilledLevers(null);
    if (selectedFields.length === 0) {
      toast({ title: "Select fields first", description: "Choose which fields the gap should be addressed in.", variant: "destructive" });
      return;
    }
    const gapTitle = recommendation.gapTitle ?? "";
    pushHistory(); setIsGenerating(true);
    setAiStatus({ state: "applying", text: `Signal AI is applying the strategy “${gapTitle}” to Variant B…` });
    try {
      const grouping = recommendation.thematicGrouping;
      const gapContext = [
        `- Gap: ${gapTitle}`,
        recommendation.consolidatedFinding ? `- Finding: ${recommendation.consolidatedFinding}` : null,
        recommendation.hypothesis ? `- Hypothesis: ${recommendation.hypothesis}` : null,
        grouping ? `- Thematic grouping: ${grouping.name}` : null,
        grouping?.keywords.length ? `- Keywords: ${grouping.keywords.map(k => k.keyword).join(", ")}` : null,
      ].filter(Boolean).join("\n");
      const result = await applyScorecardGapMutation.mutateAsync({
        productId: numericId, brandId: selectedBrandId, selectedFields, gapContext,
      });
      if (!selectedFields.some(f => result.fields[f])) {
        toast({ title: "Nothing generated", description: "The AI returned no usable content. Try a different gap.", variant: "destructive" });
        return;
      }
      // AUT-463: as for a keyword -- the applied note names only fields that differ from Variant A,
      // and when none does the strategy is not linked and the draft stays as it was.
      const appliedLabels = rewrittenFieldLabels(result.fields);
      if (appliedLabels.length === 0) {
        toast({ title: "Strategy not applied", description: `Signal AI couldn't address “${gapTitle}” in the selected fields, so Variant B was left unchanged.`, variant: "destructive" });
        return;
      }
      applyGeneratedFields(result.fields);
      setStrategyLinkId(recommendation.id);
      setLastSource("scorecard");
      setLastSourceDetail(gapTitle);
      // Built as for one applied gap whose text is the title, so useTestSubmit's derived name and
      // source detail read the same as the Content Gap flow's (`ab-test-utils.ts:buildAutoTestName`).
      setAppliedScorecardGaps([gapTitle]);
      setHypothesis(recommendation.hypothesis ?? "");
      incrementHypothesisSeq();
      setAiStatus({ state: "applied", text: `Signal AI has updated ${appliedLabels.join(", ")} in order to attempt to close the gap on “${gapTitle}”.` });
      toast({ title: "Strategy applied", description: "Variant B has been updated." });
    } catch (err) {
      toast({ title: "Generation failed", description: extractApplyErrorMessage(err), variant: "destructive" });
    } finally { setIsGenerating(false); }
  };

  const handleApplyScorecardGapQuestion = async (question: CosmoQuestion) => {
    const { status: questionStatus } = question;
    const verdictLabel = getStatusConfig(questionStatus).label;
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId || selectedFields.length === 0) return;
    // Save a gap snapshot BEFORE pushHistory so we capture the pre-gap state
    setGapSnapshots(prev => ({
      ...prev,
      [question.question]: {
        title: variantBTitle,
        bullets: { ...variantBBullets },
        appliedScorecardGaps: [...appliedScorecardGaps],
      },
    }));
    pushHistory(); setIsGenerating(true); setLastSource("scorecard"); setLastSourceDetail(question.question); setKeywordAdjustment(null); setUnfulfilledLevers(null);
    setAppliedScorecardGaps(prev => [...prev.filter(q => q !== question.question), question.question]);
    try {
      const result = await applyScorecardGapMutation.mutateAsync({
        productId: numericId, brandId: selectedBrandId, selectedFields,
        gapContext: `- Question: ${question.question}\n- Category: ${COSMO_CATEGORY_LABELS[question.category] ?? question.category}\n- Current score: ${question.score}/100 (${verdictLabel})\n- Finding: ${question.finding}\n- Recommendation: ${question.recommendation}${question.evidenceSnippet ? `\n- Evidence: ${question.evidenceSnippet}` : ""}`,
      });
      applyGeneratedFields(result.fields);
      const appliedLabels = selectedFields.map(f => f === "title" ? "title" : f).filter(f => result.fields[f]);
      const rationaleText = result.rationale || `Signal AI rewrote ${appliedLabels.join(", ")} to directly answer "${question.question}" — currently scoring ${question.score}/100 (${verdictLabel}) on the Amazon AI Score.`;
      setRationaleData({
        summary: rationaleText,
        signals: [`COSMO question: ${question.question}`, `Category: ${COSMO_CATEGORY_LABELS[question.category] ?? question.category}`, `Current score: ${question.score}/100 (${verdictLabel})`, `Recommendation: ${question.recommendation}`, `Fields rewritten: ${appliedLabels.join(", ")}`, ...(result.preserved?.length ? [`Preserved verbatim: ${result.preserved.join("; ")}`] : [])],
        phraseChanges: [], preserved: [], dropped: [],
      });
      setIsGeneratingRationale(false); setRationaleOpen(true);
      toast({ title: "Content gap applied", description: "Variant B updated to address the gap." });
    } catch {
      toast({ title: "Failed", description: "Could not apply content gap.", variant: "destructive" });
    } finally { setIsGenerating(false); }
  };

  const handleApplyFormatTreatment = async (treatment: "lead-phrase" | "length" | "hyphenation", treatmentValue: string) => {
    const numericId = getNumericProductId();
    if (!numericId || !selectedBrandId || selectedFields.length === 0) return;
    pushHistory(); setIsGenerating(true); setLastSource("ai-format"); setLastSourceDetail(`${treatment}: ${treatmentValue}`); setKeywordAdjustment(null); setUnfulfilledLevers(null);
    try {
      const result = await applyFormatTreatmentMutation.mutateAsync({ productId: numericId, brandId: selectedBrandId, selectedFields, treatment, treatmentValue });
      applyGeneratedFields(result.fields);
      toast({ title: "Treatment applied", description: `Applied ${treatment}: ${treatmentValue}` });
      triggerRationale(result.fields.title || "");
    } catch {
      toast({ title: "Failed", description: "Could not apply format treatment.", variant: "destructive" });
    } finally { setIsGenerating(false); }
  };

  return {
    handleGenerateMagic,
    handleApplySignal,
    handleApplyKeywordSource,
    handleApplyStrategy,
    handleApplyScorecardGapQuestion,
    handleApplyFormatTreatment,
    triggerRationale,
    reparseVariantBZones,
    incrementHypothesisSeq,
    keywordAdjustment,
    setKeywordAdjustment,
    unfulfilledLevers,
    setUnfulfilledLevers,
  };
}
